import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { flashBounties } from '../lib/api';
import { toast } from 'sonner';
import {
  Flame, Timer, Hotel, ArrowLeft, TrendingUp, Plus, Zap, Target, Leaf,
} from 'lucide-react';

function timeRemaining(expiresAt) {
  if (!expiresAt) return 'No expiry';
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'Expired';
  const hrs = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hrs > 24) return `${Math.floor(hrs / 24)}d ${hrs % 24}h left`;
  if (hrs > 0) return `${hrs}h ${mins}m left`;
  return `${mins}m left`;
}

function BountyCard({ bounty }) {
  const isHot = bounty.token_multiplier >= 3;
  const urgency = timeRemaining(bounty.expires_at);
  const isUrgent = urgency.includes('h ') || urgency.includes('m ');

  return (
    <div className="l-card relative overflow-hidden" data-testid="bounty-card">
      {isHot && (
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-[color:var(--l-copper)]" />
      )}
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {isHot && <Flame className="w-4 h-4 text-[color:var(--l-copper)]" />}
              <h3 className="l-display text-lg truncate">{bounty.title}</h3>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-[color:var(--l-cream-3)] mt-1">
              <Hotel className="w-3.5 h-3.5" />
              <span className="truncate">{bounty.hotel_name}</span>
              {bounty.hotel_city && (
                <span className="text-[12px] text-[color:var(--l-cream-3)]">· {bounty.hotel_city}, {bounty.hotel_country}</span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {bounty.token_multiplier > 1 && (
              <div
                data-testid="bounty-multiplier"
                className="text-[11px] tracking-wide text-[color:var(--l-bg)] bg-[color:var(--l-copper)] px-2.5 py-1 rounded-full tabular-nums"
              >
                {bounty.token_multiplier}× $HERMES
              </div>
            )}
            {bounty.commission_override_pct > 0 && (
              <div
                data-testid="bounty-commission"
                className="text-[11px] tracking-wide text-[color:var(--l-cream)] border border-[color:var(--l-line-2)] px-2.5 py-1 rounded-full tabular-nums"
              >
                {bounty.commission_override_pct}% commission
              </div>
            )}
          </div>
        </div>

        {bounty.description && (
          <p className="text-[14px] text-[color:var(--l-cream-2)] leading-relaxed mb-4">{bounty.description}</p>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-[color:var(--l-line)]">
          <div className="flex items-center gap-4 text-[12px] text-[color:var(--l-cream-3)]">
            <span className={'inline-flex items-center gap-1.5 ' + (isUrgent ? 'text-[color:var(--l-copper)]' : '')}>
              <Timer className="w-3.5 h-3.5" /> {urgency}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5" /> {bounty.bookings_generated || 0} bookings
            </span>
          </div>
          <span className="text-[11px] text-[color:var(--l-cream-3)] uppercase tracking-wide">
            {bounty.source === 'sage_ai' ? 'AI-suggested' : bounty.source === 'admin' ? 'Platform' : 'Hotel'}
          </span>
        </div>
      </div>
    </div>
  );
}

function CreateBountyDialog({ onCreated }) {
  const [openMe, setOpenMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    hotel_id: '', title: '', description: '',
    reward_type: 'token_multiplier',
    token_multiplier: 2, commission_override_pct: 0, expires_at: '',
  });

  const handleCreate = async () => {
    if (!form.hotel_id || !form.title) { toast.error('Hotel ID and title are required'); return; }
    setLoading(true);
    try {
      await flashBounties.createBounty({ ...form, created_by: 'admin', source: 'manager' });
      toast.success('Flash rate activated.');
      setOpenMe(false);
      setForm({ hotel_id: '', title: '', description: '', reward_type: 'token_multiplier', token_multiplier: 2, commission_override_pct: 0, expires_at: '' });
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create bounty');
    } finally {
      setLoading(false);
    }
  };

  const input = 'l-input w-full px-4 py-3 text-[15px] outline-none';

  return (
    <Dialog open={openMe} onOpenChange={setOpenMe}>
      <DialogTrigger asChild>
        <button className="l-btn-copper h-10 px-4 text-sm inline-flex items-center gap-1.5" data-testid="create-bounty-button">
          <Plus className="w-4 h-4" /> Create
        </button>
      </DialogTrigger>
      <DialogContent
        className="bg-[color:var(--l-bg-2)] border border-[color:var(--l-line-2)] text-[color:var(--l-cream)] rounded-2xl max-w-md"
        data-testid="create-bounty-modal"
      >
        <DialogHeader>
          <DialogTitle className="l-display text-xl inline-flex items-center gap-2">
            <Zap className="w-5 h-5 text-[color:var(--l-copper)]" /> Activate a flash rate
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-[13px] text-[color:var(--l-cream-2)] mb-1.5">Hotel ID</label>
            <input value={form.hotel_id} onChange={(e) => setForm((f) => ({ ...f, hotel_id: e.target.value }))}
              placeholder="hotel_grand_marina" data-testid="bounty-hotel-id" className={input} />
          </div>
          <div>
            <label className="block text-[13px] text-[color:var(--l-cream-2)] mb-1.5">Title</label>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Weekend flash: 2× $HERMES" data-testid="bounty-title" className={input} />
          </div>
          <div>
            <label className="block text-[13px] text-[color:var(--l-cream-2)] mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Fill our last 10 rooms this weekend…" rows={2}
              data-testid="bounty-description" className={input + ' resize-none'} />
          </div>
          <div>
            <label className="block text-[13px] text-[color:var(--l-cream-2)] mb-1.5">Reward type</label>
            <Select value={form.reward_type} onValueChange={(v) => setForm((f) => ({ ...f, reward_type: v }))}>
              <SelectTrigger
                data-testid="bounty-reward-type"
                className="l-input h-11 px-4 text-[15px] text-[color:var(--l-cream)]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[color:var(--l-bg-2)] border-[color:var(--l-line-2)] text-[color:var(--l-cream)]">
                <SelectItem value="token_multiplier">Token multiplier</SelectItem>
                <SelectItem value="commission_override">Commission override</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(form.reward_type === 'token_multiplier' || form.reward_type === 'both') && (
            <div>
              <label className="block text-[13px] text-[color:var(--l-cream-2)] mb-1.5">Token multiplier</label>
              <input type="number" min="1" step="0.5" value={form.token_multiplier}
                onChange={(e) => setForm((f) => ({ ...f, token_multiplier: parseFloat(e.target.value) || 1 }))}
                data-testid="bounty-multiplier-input" className={input} />
            </div>
          )}
          {(form.reward_type === 'commission_override' || form.reward_type === 'both') && (
            <div>
              <label className="block text-[13px] text-[color:var(--l-cream-2)] mb-1.5">Commission override (%)</label>
              <input type="number" min="1" max="50" step="1" value={form.commission_override_pct}
                onChange={(e) => setForm((f) => ({ ...f, commission_override_pct: parseFloat(e.target.value) || 0 }))}
                data-testid="bounty-commission-input" className={input} />
            </div>
          )}
          <div>
            <label className="block text-[13px] text-[color:var(--l-cream-2)] mb-1.5">Expires at</label>
            <input type="datetime-local" value={form.expires_at?.slice(0, 16) || ''}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
              data-testid="bounty-expires-at" className={input} />
          </div>
          <button
            onClick={handleCreate}
            disabled={loading}
            className="l-btn-copper h-11 w-full text-sm disabled:opacity-60"
            data-testid="submit-bounty"
          >
            {loading ? 'Activating…' : 'Activate'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BountyBoardPage() {
  const navigate = useNavigate();
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flashBounties.getBoard();
      setBounties(res.data?.bounties || []);
    } catch {
      toast.error('Failed to load rewards');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  const hotBounties = bounties.filter((b) => b.token_multiplier >= 3);
  const regularBounties = bounties.filter((b) => b.token_multiplier < 3);
  const maxMultiplier = bounties.length ? Math.max(...bounties.map((b) => b.token_multiplier || 1)) : 0;
  const totalBookings = bounties.reduce((s, b) => s + (b.bookings_generated || 0), 0);

  return (
    <div className="lobby-theme min-h-screen flex flex-col" data-testid="bounty-board-page">
      <div className="absolute inset-0 lobby-glow pointer-events-none" />
      <div className="lobby-grain" />

      {/* Nav */}
      <nav className="relative z-10 border-b border-[color:var(--l-line)] bg-[color:var(--l-bg)]/80 backdrop-blur-md sticky top-0">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} data-testid="bounty-back"
            className="flex items-center gap-2.5 text-[color:var(--l-cream-2)] hover:text-[color:var(--l-cream)] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[color:var(--l-copper)] flex items-center justify-center">
              <span className="l-display text-[color:var(--l-bg)] text-xs">b</span>
            </div>
            <span className="l-display text-[15px]">getbitsy</span>
          </Link>
          <CreateBountyDialog onCreated={loadBoard} />
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 border-b border-[color:var(--l-line)] py-14 sm:py-20">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8">
          <div className="max-w-2xl lobby-reveal">
            <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">Rewards</div>
            <h1 className="l-display text-4xl sm:text-5xl lg:text-6xl">
              Quiet nights,<br />
              <span className="text-[color:var(--l-copper)]">loud rewards</span>.
            </h1>
            <p className="mt-5 text-[color:var(--l-cream-2)] max-w-xl leading-relaxed">
              When hotels need rooms filled fast, they boost the payout. Share the stay, send a friend, or book it yourself at a better rate than direct.
            </p>
          </div>

          {/* KPI row */}
          <div className="mt-10 grid grid-cols-3 gap-px bg-[color:var(--l-line)] border border-[color:var(--l-line)] rounded-xl overflow-hidden max-w-xl">
            {[
              { l: 'Active now',     v: bounties.length,                    tid: 'total-active-bounties' },
              { l: 'Top multiplier', v: maxMultiplier ? `${maxMultiplier}×` : '—' },
              { l: 'Bookings so far',v: totalBookings },
            ].map((k) => (
              <div key={k.l} className="bg-[color:var(--l-bg-2)] px-4 py-3">
                <div className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--l-cream-3)]">{k.l}</div>
                <div className="l-display text-2xl sm:text-3xl text-[color:var(--l-copper)] tabular-nums mt-1" data-testid={k.tid}>{k.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bounties */}
      <section className="relative z-10 flex-1 py-14 sm:py-20">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8">
          {loading ? (
            <div className="text-center py-16 text-[color:var(--l-cream-2)]">Loading rewards…</div>
          ) : bounties.length === 0 ? (
            <div className="text-center py-16 max-w-md mx-auto">
              <Flame className="w-10 h-10 text-[color:var(--l-cream-3)] mx-auto mb-4" />
              <h2 className="l-display text-2xl">All quiet for now.</h2>
              <p className="text-[color:var(--l-cream-2)] mt-2 leading-relaxed">
                No hotels have flagged rooms to fill. Check back soon — these light up when a hotel needs help.
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {hotBounties.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-5">
                    <Flame className="w-4 h-4 text-[color:var(--l-copper)]" />
                    <h2 className="l-display text-lg uppercase tracking-[0.14em] text-[color:var(--l-copper)]">Hot right now</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {hotBounties.map((b) => <BountyCard key={b.id} bounty={b} />)}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center gap-2 mb-5">
                  <TrendingUp className="w-4 h-4 text-[color:var(--l-cream-2)]" />
                  <h2 className="l-display text-lg uppercase tracking-[0.14em] text-[color:var(--l-cream-2)]">
                    {hotBounties.length > 0 ? 'Also worth a look' : 'Active rewards'}
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {regularBounties.map((b) => <BountyCard key={b.id} bounty={b} />)}
                </div>
                {regularBounties.length === 0 && hotBounties.length > 0 && (
                  <p className="text-sm text-[color:var(--l-cream-3)] text-center py-4">All active rewards are hot right now.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <footer className="relative z-10 border-t border-[color:var(--l-line)] py-6">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 flex items-center justify-center text-[12px] text-[color:var(--l-cream-3)]">
          <span className="inline-flex items-center gap-2">
            <Leaf className="w-3.5 h-3.5 text-[color:var(--l-copper)]" /> Hospitality, quietly rebuilt.
          </span>
        </div>
      </footer>
    </div>
  );
}
